import type {FunctionComponent, SyntheticEvent} from 'react'
import React from 'react'
import {useOutletContext, useRouteLoaderData, useSubmit} from '@remix-run/react'
import clsx from 'clsx'
import type {RootAndListContext, RootLoaderResponse, TopDiscountsOnTopRatedCategoris} from '~/types'
import {CLICK_ID_PLACEHOLDER} from '~/utils/affiliation'
import {useReportItemSeen, useReportWidgetClick, useSendPartnerProductClickoutCustom} from '~/hooks/analytic-hooks'
import {getDomainConfig} from '~/domains'
import {getSchemaPriceValidUntil, objectToQueryString} from '~/utils/commons'
import {grantAllConsent} from '~/utils/consent-manager.client'
import DiscountCornerRibbon from '../../badges/discount-corner-ribbon'

interface TopRatedDiscountedCardProps {
  productInfo: TopDiscountsOnTopRatedCategoris
  index: number
  dealsLength: number
}

const TopRatedDiscountedCard: FunctionComponent<TopRatedDiscountedCardProps> = ({productInfo, index, dealsLength}) => {
  const {image, title, placement, discount, slug, affiliateLink, asin, price} = productInfo
  const {locale, featureFlags, trackAnalytics, currencyCode, hostname, domain, traffic, renderTimestamp} =
    useRouteLoaderData('root') as RootLoaderResponse
  const {clickId, setShowCookieConsentGdpr} = useOutletContext<RootAndListContext>()
  const {styles: stylesBydomain} = getDomainConfig(domain, locale)
  const submit = useSubmit()

  const spanMap: Record<number, (index: number) => string> = {
    1: () => 'col-span-2',
    2: () => 'col-span-2',
    3: (i) => (i === 0 ? 'col-span-2' : ''),
    4: () => '',
  }
  const spanClass = spanMap[dealsLength]?.(index) ?? ''

  const [cardRef] = useReportItemSeen({
    item_list_id: slug,
    item_name: title,
    item_source: 'list',
    partner_item_brand: title,
    partner_item_id: asin,
    affiliation: affiliateLink.type,
    is_dws: false,
    price,
    currency: currencyCode,
    click_element_name: 'top-rated-discounted',
    index: index + 1,
  })

  const reportWidgetClick = useReportWidgetClick()

  const reportClickout = useSendPartnerProductClickoutCustom()

  const shouldForceTrackAnalytics = Boolean(
    featureFlags?.cookieConsentVariation === 'opt-in' &&
    (!trackAnalytics?.permission || trackAnalytics?.permission === 'denied'),
  )

  const trackSearchParams = objectToQueryString({
    session_id: trackAnalytics?.session_id,
    ...trackAnalytics?.pageLoadData,
    page_id: trackAnalytics?.page_id,
    user_id: trackAnalytics?.user_id,
  })

  const affiliateLinkWithClickId = affiliateLink.url.replaceAll(CLICK_ID_PLACEHOLDER, clickId)

  const redirectUrl = shouldForceTrackAnalytics
    ? `/redirect-analytics?affiliateLink=${encodeURIComponent(affiliateLinkWithClickId)}&${trackSearchParams}`
    : affiliateLinkWithClickId

  const handleCardClick = (_e: SyntheticEvent, elementName: 'image' | 'title' | 'price' | 'cta') => {
    if (shouldForceTrackAnalytics) {
      setShowCookieConsentGdpr(false)
      grantAllConsent()
      submit(
        {
          'accept-gdpr': 'granted',
          redirect: 'true',
          'track-analytics-data': JSON.stringify({...trackAnalytics, permission: 'granted'}),
        },
        {method: 'POST', action: '/cookie-consent', replace: false, navigate: false},
      )
    }

    reportClickout({
      index: index + 1,
      item_list_id: slug,
      click_element_name: `top-rated-discounted-${index + 1}`,
      partner_item_brand: title,
      partner_item_id: asin,
      item_name: title,
      item_brand: title,
      click_id: affiliateLink?.clickId,
      affiliation: affiliateLink?.type,
      is_dws: true,
      location_id: locale,
      traffic: traffic || 'unknown',
      gclid: undefined,
      sys_id: undefined,
      target: affiliateLinkWithClickId,
    })
    reportWidgetClick({
      widget_name: 'top-rated-discounted',
      widget_place: `${index + 1}`,
      trigger_type: elementName,
    })

    if (typeof Storage != 'undefined') {
      localStorage.setItem('clickTime', Date.now().toString())
      if (featureFlags?.showHustonProblemPopup) localStorage.setItem('redirectUrl', redirectUrl)
    }
  }

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org/',
            '@type': 'Product',
            name: title,
            image: [image],
            description: title,
            brand: {
              '@type': 'Brand',
              name: title,
            },
            offers: {
              '@type': 'Offer',
              url: `https://${hostname}${affiliateLinkWithClickId}`,
              priceCurrency: currencyCode,
              price: price,
              priceValidUntil: getSchemaPriceValidUntil(renderTimestamp, 'en-CA'),
              itemCondition: 'https://schema.org/NewCondition',
              availability: 'https://schema.org/InStock',
              seller: {
                '@type': 'Organization',
                name: title ?? 'Amazon',
              },
            },
          }),
        }}
      ></script>
      <div className={clsx('relative flex w-full flex-col gap-2', spanClass)} ref={cardRef}>
        <a
          className="absolute inset-0 z-20"
          href={redirectUrl}
          onClick={(e) => handleCardClick(e, 'image')}
          aria-label={`view ${slug}`}
          target="_blank"
          rel="sponsored noreferrer"
        ></a>
        <DiscountCornerRibbon className="absolute -left-[3px] -top-[3px] [&>div>.ribbon]:size-20" discount={discount} />
        <div
          className={clsx(
            'absolute right-2 top-2 rounded-full px-1.5 text-xs',
            stylesBydomain.LISTPAGE_BADGE_PLACEMENT_BG,
            stylesBydomain.LISTPAGE_BADGE_PLACEMENT_TEXT,
            'listpage_badge_placement_bg listpage_badge_placement_text',
          )}
        >
          #{placement}
        </div>
        <div className="flex h-[138px] items-center justify-center rounded-xl bg-white">
          <img src={image} alt={title} className="size-24 object-scale-down" loading="eager" width="96" height="96" />
        </div>
        <span className="line-clamp-1 text-xs text-white">{title}</span>
      </div>
    </>
  )
}

export default TopRatedDiscountedCard
